import React from 'react'
import { Trash2, RefreshCw, Clock } from 'lucide-react'
import { formatFileSize } from '../utils/fileHelpers'

interface TrashItemProps {
  id: string
  name: string
  type: 'file' | 'folder'
  size?: number
  path: string
  deletedAt?: string
  onRestore: (id: string) => void
  onDelete: (id: string) => void
}

const TrashItem: React.FC<TrashItemProps> = ({
  id,
  name,
  type,
  size,
  path,
  deletedAt,
  onRestore,
  onDelete
}) => {
  const getDaysRemaining = (): number => {
    if (!deletedAt) return 0
    
    const deletedDate = new Date(deletedAt)
    const expiryDate = new Date(deletedDate)
    expiryDate.setDate(expiryDate.getDate() + 30)
    
    const now = new Date()
    const diffTime = expiryDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    return Math.max(0, diffDays)
  }
  
  const daysRemaining = getDaysRemaining()
  
  // Custom folder icon with solid fill
  const FolderIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="var(--color-secondary)" 
      stroke="var(--color-secondary)" 
      strokeWidth="0.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
    </svg>
  )
  
  return (
    <div className="bg-white dark:bg-dark-background rounded-lg shadow-custom-light p-4 transition-all duration-300 hover:shadow-custom-medium">
      <div className="flex items-start">
        <div className="flex-shrink-0 mr-4">
          <div className="h-12 w-12 flex items-center justify-center bg-[var(--color-background)] rounded-md">
            {type === 'folder' ? (
              <FolderIcon />
            ) : (
              <Trash2 size={24} className="text-[var(--color-primary)]" />
            )}
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-medium truncate" title={name}>{name}</h3>
          <p className="text-sm text-[var(--color-accent)] truncate" title={path}>
            Original location: {path}
          </p>
          <div className="flex items-center mt-1">
            <Clock size={16} className={`mr-1 ${
              daysRemaining <= 5 ? 'text-red-500' : 
              daysRemaining <= 10 ? 'text-yellow-500' : 
              'text-[var(--color-accent)]'
            }`} />
            <span className={`text-sm ${
              daysRemaining <= 5 ? 'text-red-500 font-medium' : 
              daysRemaining <= 10 ? 'text-yellow-500 font-medium' : 
              'text-[var(--color-accent)]'
            }`}>
              {daysRemaining} days left
            </span>
          </div>
          {type === 'file' && size !== undefined && (
            <p className="text-sm text-[var(--color-accent)] mt-1">
              Size: {formatFileSize(size)}
            </p>
          )}
        </div>
        
        <div className="flex-shrink-0 ml-4 flex flex-col space-y-2">
          <button
            className="p-2 text-[#4C704C] hover:bg-[var(--color-background)] rounded flex items-center"
            onClick={() => onRestore(id)}
            title="Restore"
          >
            <RefreshCw size={16} className="mr-2" />
            <span>Restore</span>
          </button>
          <button
            className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded flex items-center"
            onClick={() => onDelete(id)}
            title="Delete permanently"
          >
            <Trash2 size={16} className="mr-2" />
            <span>Delete</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default TrashItem